import React, { useEffect, useRef, useState, useMemo } from "react";
import { IonIcon, IonPage } from "@ionic/react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  Button,
  Tabs,
  Typography,
  Space,
  Row,
  Col,
  Form,
  Input,
  Breadcrumb,
  Result,
  Spin,
  notification,
  Card,
  Modal,
  Tag,
  Divider,
} from "antd";
import { MailOutlined, Loading3QuartersOutlined } from "@ant-design/icons";

//
import log from "../../../utils/log";
import c from "../../../utils/common";
import moment from "moment";
import _ from "lodash";
import { showErrMsg } from "../../../utils/error";
import permissionMetaData from "../../../data/permission";
import { isSuper } from "../../../utils/permission";

//
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/GetStarted/SectionHeader";
import Name from "../../../components/General/Name";

//
import useAuthStore from "../../../states/useAuth";
import useReferralQuery from "../../../hooks/useQuery/useReferral";
import useReferral from "../../../hooks/useReferral";
import Description from "../../../components/General/Description";
import PeriodPicker from "../../../components/General/PeriodPicker";
import dayjs from "../../../utils/day";
import useCompanyStore from "../../../states/useCompany";
import ItemSelectionDrawer from "../../../components/ItemSelectionDrawer";
import useProductQuery from "../../../hooks/useQuery/useProduct";
import VoucherDetailDrawer from "../../../components/Referral/VoucherDetailDrawer";
import Number from "../../../components/General/Number";
import { formatProductWithAvailableQty, getReferralStatus } from "../../../utils/referral";
import useEstamp from "../../../hooks/useEstamp";
import { getPeriodStatus } from "../../../utils/estamp";
import GiftTable from "../../../components/Estamp/GiftTable";
import VoucherTable from "../../../components/Referral/VoucherTable";
import { formatTime } from "../../../utils/time";
import ReferralStatus from "../../../components/Table/ReferralStatus";
import { arrowForwardOutline } from "ionicons/icons";

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const SingleReferral = () => {
  const { id } = useParams(); // Get the :id parameter from the URL
  const history = useHistory();
  const location = useLocation();

  //
  const { getMaxShopExpiry } = useCompanyStore();
  const { auth, user } = useAuthStore();
  const { createReferral, updateReferral } = useReferral();
  const { getSingleEstamp } = useEstamp();

  //Sider
  const [collapsed, setCollapsed] = useState(false);

  //Form
  const [referralForm] = Form.useForm();
  const [newReferral, setNewReferral] = useState();

  //
  const [originalReferral, setOriginalReferral] = useState();
  const [referral, setReferral] = useState();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  //referral
  const [ongoingPeriod, setOngoingPeriod] = useState();
  const [activeTab, setActiveTab] = useState("referrer");
  const tabs = [
    {
      key: "referrer",
      title: "Referrer Reward",
    },
    {
      key: "referee",
      title: "Referee Reward",
    },
  ];

  const isActionBtnDisabled =
    id == "new"
      ? !c.hasDiff(originalReferral, newReferral)
      : !originalReferral || !c.hasDiff(originalReferral, referral);

  const canEdit = id == "new" || (id != "new" && getReferralStatus(referral) == "pending");

  const now = useMemo(() => moment().toISOString(), []);

  const {
    data: referrals = [],
    isFetching: isFetchingReferrals,
    error: referralsError,
    refetch: refetchReferrals,
  } = useReferralQuery("referrals", { "time.collect.end": { $gt: now } });

  const {
    data: singleReferral,
    isFetching: isFetchingSingleReferral,
    error: referralError,
    refetch: refetchSingleReferral,
  } = useReferralQuery(
    "referral",
    { id: id },
    {
      enabled: !!id && id !== "new" && !!user?.companyId,
      staleTime: Infinity, // Prevents refetching on component remounts or when coming back to the page
      cacheTime: 0, // Keeps data fresh only until the component is unmounted
    }
  );

  // Set original and current promotion referral whenever new data is fetched
  useEffect(() => {
    setOriginalReferral(singleReferral);
    setReferral(singleReferral);
    // prepare period data if exist
    if (singleReferral?.referrerCoin?.period) {
      setOngoingPeriod(singleReferral.referrerCoin.period);
    } else if (singleReferral?.refereeCoin?.period) {
      setOngoingPeriod(singleReferral.refereeCoin.period);
    }
  }, [singleReferral]);

  //
  const referralEndTime = id == "new" ? newReferral?.time?.collect?.end : referral?.time?.collect?.end;
  const {
    data: products = [],
    isFetching: isFetchingProducts,
    refetch: refetchProducts,
  } = useProductQuery(
    {
      type: "voucher",
      companyId: user?.companyId,
    },
    {
      enabled: !!referralEndTime, // Ensure the query is only enabled when referralEndTime is defined
    }
  );

  const filteredProducts = useMemo(() => {
    // Map filtered products to include qty.available
    return products.map((product, i) => {
      // Inject qty.available using formatProductWithAvailableQty
      const productWithQty = formatProductWithAvailableQty(product, referrals);

      // Add a unique key and return the updated product
      return { ...productWithQty, key: i };
    });
  }, [products, referrals]);

  const getEstampPeriod = async (userType) => {
    const result = await getSingleEstamp(user?.companyId, false, false);
    const ongoingPeriod = result.periods?.find((e) => getPeriodStatus(e) === "ongoing");

    if (!ongoingPeriod || !ongoingPeriod.time?.earn?.start || !ongoingPeriod.time?.earn?.end) {
      log.debug("No valid ongoing coin found");
      return Modal.warn({ title: "No valid ongoing coin found" });
    }

    const collectTime = id == "new" ? newReferral?.time?.collect : singleReferral?.time?.collect;
    if (!collectTime || !collectTime.start || !collectTime.end) {
      log.debug("newReferral.time.collect is not fully defined.");
      return Modal.warn({ title: "Please select a valid period" });
    }

    // Check if `collectTime.start` and `collectTime.end` are within `ongoingPeriod.time.earn`
    const isWithinPeriod =
      moment(collectTime.start).isBetween(
        ongoingPeriod.time.earn.start,
        ongoingPeriod.time.earn.end,
        undefined,
        "[]"
      ) &&
      moment(collectTime.end).isBetween(ongoingPeriod.time.earn.start, ongoingPeriod.time.earn.end, undefined, "[]");

    if (!isWithinPeriod) {
      return Modal.warn({ title: "This referral is not within any ongoing coin earning period" });
    }

    setOngoingPeriod(ongoingPeriod);
    updateReferralData({
      [userType === "referrer" ? "referrerCoin" : "refereeCoin"]: { periodId: ongoingPeriod.id, amount: 0 },
    });
  };

  const submitForm = async () => {
    //check form
    try {
      await referralForm.validateFields();
    } catch (error) {
      log.error(error);
      return notification.error({
        message: "error",
        description: "Some of the input fields are invalid. Please review and save again",
      });
    }

    let before = id == "new" ? newReferral : referral;

    if (!before?.referrerProducts?.length || !before?.refereeProducts?.length) {
      return notification.error({ message: "Add at least one voucher" });
    }

    if (
      (before?.referrerCoin?.periodId && !before?.referrerCoin?.amount > 0) ||
      (before?.refereeCoin?.periodId && !before?.refereeCoin?.amount > 0)
    ) {
      return notification.error({ message: "Coin amount cannot be zero" });
    }

    // Get the diff between originalReferral and request
    let diff = c.getObjectDiff(originalReferral, before);
    console.log("Changed fields:", diff);

    if (!diff) {
      return notification.error({ message: "no information changed" });
    }

    // axios
    try {
      setIsLoadingSubmit(true);

      let response;
      if (id === "new") {
        response = await createReferral(diff);
      } else {
        response = await updateReferral(before.id, diff);
      }

      // Update data
      setOriginalReferral(response);
      setReferral(response);

      // Show success notification
      notification.success({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Success
          </Title>
        ),
      });

      //redirect
      if (id === "new") {
        history.replace(`/merchantUser/referral/${response.id}`);
      }
    } catch (error) {
      let message = error.response ? error.response.data : error.message;
      if (message.includes("Insufficient quota")) {
        notification.error({
          message: (
            <Title style={{ margin: 0 }} level={4}>
              Insufficient account quota
            </Title>
          ),
          description: <Text strong>Please contact us for more quota</Text>,
        });
      } else {
        showErrMsg(error);
      }
    } finally {
      setIsLoadingSubmit(false); // Ensure loading state is cleared
    }
  };

  const updateReferralData = (newData) => {
    log.debug(newData);

    const update = (prev) => {
      let updated = _.merge({}, prev, newData); // Merge as usual for other updates

      // delete
      if ("referrerCoin" in newData && newData.referrerCoin === undefined) {
        updated.referrerCoin = undefined;
      }

      if ("refereeCoin" in newData && newData.refereeCoin === undefined) {
        updated.refereeCoin = undefined;
      }

      // overwrite
      if ("referrerProducts" in newData) {
        updated.referrerProducts = newData.referrerProducts;
      }
      if ("refereeProducts" in newData) {
        updated.refereeProducts = newData.refereeProducts;
      }

      log.debug(updated);
      return updated;
    };

    if (id === "new") {
      setNewReferral(update);
    } else {
      setReferral(update);
    }
  };

  return (
    <div style={{ margin: "0 auto" }}>
      <Spin
        spinning={isFetchingSingleReferral}
        // percent="auto"
        indicator={<Loading3QuartersOutlined spin style={{ color: "white", fontSize: "2rem" }} />}
        fullscreen
      />

      {referralError ? (
        <Result
          status="404"
          title="404"
          subTitle="Sorry, the page you visited does not exist."
          extra={
            <Button
              type="primary"
              onClick={() => {
                window.location.reload();
              }}
            >
              Refresh
            </Button>
          }
        />
      ) : (
        <>
          <Breadcrumb
            items={[
              {
                title: (
                  <a
                    onClick={() => {
                      history.push("/merchantUser/referral");
                    }}
                  >
                    Referral List
                  </a>
                ),
              },
              {
                title: <a>{id == "new" ? "New Referral" : "Referral Detail"}</a>,
              },
            ]}
          />

          <PageHeader
            title={id == "new" ? "New Referral" : "Referral Detail"}
            isSticky
            actionGroup={
              <Space>
                <Button
                  size="large"
                  disabled={isActionBtnDisabled}
                  loading={isLoadingSubmit}
                  color="default"
                  variant="filled"
                  style={{ marginRight: "1.5rem", width: "7.5rem" }}
                  onClick={async () => {
                    //reset
                    if (id == "new") {
                      setNewReferral(originalReferral);
                    } else {
                      setReferral(originalReferral);
                    }
                  }}
                >
                  Reset
                </Button>

                <Button
                  size="large"
                  type="primary"
                  disabled={isActionBtnDisabled}
                  loading={isLoadingSubmit}
                  style={{ width: "7.5rem" }}
                  onClick={() => {
                    submitForm();
                  }}
                >
                  {id == "new" ? "Create Referral" : "Update"}
                </Button>
              </Space>
            }
            onBack={() => {
              history.goBack();
            }}
          />

          <Form form={referralForm} style={{ width: "100%", marginTop: "2rem" }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Row
                  style={{
                    marginBottom: "1rem",
                  }}
                >
                  <Row style={{ width: "100%" }}>
                    <SectionHeader
                      title="Game Rules"
                      description="Referral campaign allows users to share referral code with friends! When a friend signs up successfully, both will receive a one-time reward. First come first serve."
                      style={{
                        width: "50%",
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                      }}
                    />
                  </Row>

                  <Row align="top" style={{ marginTop: "1rem" }}>
                    <SectionHeader title=" Valid Period" />
                    <Col span={24}>
                      {id != "new" ? <ReferralStatus referral={referral} style={{ marginRight: "1rem" }} /> : null}

                      {id == "new" || (id != "new" && getReferralStatus(referral) == "pending") ? (
                        <PeriodPicker
                          state={{
                            data: id === "new" ? newReferral?.time?.collect : referral?.time?.collect,
                            form: referralForm,
                            rules: [{ required: true, message: "required" }],
                            onChange: (updated) => {
                              const currentCollect = (id === "new" ? newReferral : referral)?.time?.collect;
                              const hasExistingPeriod = currentCollect?.start && currentCollect?.end;

                              if (hasExistingPeriod) {
                                Modal.confirm({
                                  title: "Confirm?",
                                  content:
                                    "Changing the period will reset products and coins. Do you want to continue?",
                                  okText: "Yes",
                                  cancelText: "No",
                                  onOk: () => {
                                    updateReferralData({
                                      time: {
                                        ...((id === "new" ? newReferral : referral)?.time || {}),
                                        collect: updated,
                                      },
                                      referrerCoin: undefined,
                                      refereeCoin: undefined,
                                      referrerProducts: [],
                                      refereeProducts: []
                                    });
                                    setOngoingPeriod();
                                  },
                                  onCancel: () => {
                                    // revert UI to old value to keep sync
                                    referralForm.setFieldsValue({
                                      collectPeriod: [dayjs(currentCollect.start), dayjs(currentCollect.end)],
                                    });
                                  },
                                });
                              } else {
                                // no existing period, update immediately
                                updateReferralData({
                                  time: {
                                    ...((id === "new" ? newReferral : referral)?.time || {}),
                                    collect: updated,
                                  },
                                  referrerCoin: undefined,
                                  refereeCoin: undefined,
                                });
                                setOngoingPeriod();
                              }
                            },
                            keyName: "collectPeriod",
                          }}
                          minDate={dayjs()}
                          maxDate={getMaxShopExpiry()}
                          style={{ width: "100%", marginTop: "1rem" }}
                        />
                      ) : !referral?.time?.collect ? null : (
                        <Text strong>
                          {`${formatTime(referral.time.collect.start, "YYYY-MM-DD")} to ${formatTime(
                            referral.time.collect.end,
                            "YYYY-MM-DD"
                          )}`}
                        </Text>
                      )}
                    </Col>
                  </Row>
                </Row>
              </Col>

              {newReferral?.time || referral?.time ? (
                <>
                  {/* Referrer Section */}
                  <Col span={12}>
                    <Card
                      title={<SectionHeader title="Referrer Reward" description="At least 1 type of voucher" />}
                      style={{
                        margin: "1rem 0 1rem 0",
                        border: "2px solid var(--ion-color-step-200)",
                      }}
                    >
                      {/* Coin */}
                      <CoinSection
                        state={{
                          data: id === "new" ? newReferral?.referrerCoin : referral?.referrerCoin,
                          form: referralForm,
                          onChange: (value) => updateReferralData({ referrerCoin: { amount: value } }),
                          keyName: "referrerCoin.amount",
                        }}
                        ongoingPeriod={ongoingPeriod}
                        canEdit={canEdit}
                        getEstampPeriod={() => getEstampPeriod("referrer")}
                        onRemove={() => {
                          updateReferralData({ referrerCoin: undefined });
                        }}
                      />

                      <Divider />

                      {/* Voucher */}
                      <Vouchers
                        state={{
                          data: id === "new" ? newReferral?.referrerProducts : referral?.referrerProducts,
                          form: referralForm,
                        }}
                        products={filteredProducts}
                        referralEndTime={referralEndTime}
                        canEdit={canEdit}
                        isFetchingProducts={isFetchingProducts}
                        isLoadingSubmit={isLoadingSubmit}
                        onSave={(updatedProducts) => {
                          updateReferralData({ referrerProducts: updatedProducts });
                        }}
                        onDelete={(productId) => {
                          const updatedProducts =
                            id === "new"
                              ? newReferral.referrerProducts.filter((product) => product.id !== productId)
                              : referral.referrerProducts.filter((product) => product.id !== productId);

                          updateReferralData({ referrerProducts: updatedProducts });
                        }}
                      />
                    </Card>
                  </Col>

                  {/* Referee Section */}
                  <Col span={12}>
                    <Card
                      title={<SectionHeader title="Referee Reward" description="At least 1 type of voucher" />}
                      style={{
                        margin: "1rem 0 1rem 0",
                        border: "2px solid var(--ion-color-step-200)",
                      }}
                    >
                      {/* Coin */}
                      <CoinSection
                        state={{
                          data: id === "new" ? newReferral?.refereeCoin : referral?.refereeCoin,
                          form: referralForm,
                          onChange: (value) => updateReferralData({ refereeCoin: { amount: value } }),
                          keyName: "refereeCoin.amount",
                        }}
                        ongoingPeriod={ongoingPeriod}
                        canEdit={canEdit}
                        getEstampPeriod={() => getEstampPeriod("referee")}
                        onRemove={() => {
                          updateReferralData({ refereeCoin: undefined });
                        }}
                      />

                      <Divider />

                      {/* Voucher */}
                      <Vouchers
                        state={{
                          data: id === "new" ? newReferral?.refereeProducts : referral?.refereeProducts,
                          form: referralForm,
                        }}
                        products={filteredProducts}
                        referralEndTime={referralEndTime}
                        canEdit={canEdit}
                        isFetchingProducts={isFetchingProducts}
                        isLoadingSubmit={isLoadingSubmit}
                        onSave={(updatedProducts) => {
                          updateReferralData({ refereeProducts: updatedProducts });
                        }}
                        onDelete={(productId) => {
                          const updatedProducts =
                            id === "new"
                              ? newReferral.refereeProducts.filter((product) => product.id !== productId)
                              : referral.refereeProducts.filter((product) => product.id !== productId);

                          updateReferralData({ refereeProducts: updatedProducts });
                        }}
                      />
                    </Card>
                  </Col>
                </>
              ) : null}
            </Row>
          </Form>
        </>
      )}
    </div>
  );
};

const CoinSection = ({ state, ongoingPeriod, canEdit, getEstampPeriod, onRemove }) => {
  const { data, form, onChange, keyName } = state;

  return (
    <>
      <Row justify="space-between" align="middle" style={{ width: "100%" }}>
        <Col>
          <SectionHeader title="Add Coin" description="optional" />
        </Col>
        <Col>
          {canEdit && data?.periodId ? (
            <Button
              size="large"
              color="danger"
              variant="outlined"
              onClick={() => {
                onRemove();
              }}
            >
              remove
            </Button>
          ) : (
            <Button
              size="large"
              type="primary"
              disabled={!canEdit}
              onClick={() => {
                getEstampPeriod();
              }}
            >
              Add+
            </Button>
          )}
        </Col>
      </Row>

      {ongoingPeriod && data ? (
        <>
          <Tag color="green">Ongoing</Tag>
          <SectionHeader title={ongoingPeriod.name} description={`$${ongoingPeriod.rate} Earn 1 Coin`} />

          <Title level={5} style={{ marginTop: "2rem" }}>
            Amount
          </Title>
          <Number
            state={{
              data: data?.amount,
              form: form,
              rules: [
                { required: true, message: "required" },
                {
                  validator: (_, value) =>
                    value > 0 ? Promise.resolve() : Promise.reject(new Error("Must be greater than zero")),
                },
              ],
              onChange: onChange,
            }}
            min={1}
            keyName={keyName}
            readOnly={!canEdit}
          />
        </>
      ) : (
        <Text type="secondary">No coin selected.</Text>
      )}
    </>
  );
};

const Vouchers = ({
  state = {},
  products = [],
  referralEndTime,
  canEdit,
  isFetchingProducts,
  isLoadingSubmit,
  onSave,
  onDelete,
}) => {
  const { data = [], form } = state;
  const { id } = useParams(); // Get the :id parameter from the URL
  const history = useHistory();

  //
  const [isShowVoucherDrawer, setIsShowVoucherDrawer] = useState(false);
  const [isShowVoucherDetailDrawer, setIsShowVoucherDetailDrawer] = useState(false);
  //
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [voucher, setVoucher] = useState();

  const product = useMemo(() => {
    return selectedProductIds.length > 0 ? products.find((e) => e.id == selectedProductIds[0]) : undefined;
  }, [voucher, selectedProductIds, products]);

  const productsToSelect = useMemo(() => {
    return products.filter(
      (e) =>
        e?.qty?.available > 0 &&
        !data.map((x) => x.id).includes(e.id) &&
        e?.isReferral &&
        moment(e?.time?.redeem?.end).isSameOrAfter(moment(referralEndTime))
    );
  }, [data, products]);

  const tableData = useMemo(() => {
    return data.map((voucher) => {
      // Find the product matching the voucher's productId
      const product = products.find((prod) => prod.id === voucher.id);

      // Return the product along with qtyPerCollection (from the voucher) added to it
      return {
        ...product, // Include all product properties
        qtyPerCollection: voucher.qtyPerCollection || 0, // Add qtyPerCollection from voucher (default to 0 if undefined)
      };
    });
  }, [products, data]); // Recalculate whenever products or data changes

  const onSelectChange = (productIds) => {
    setSelectedProductIds(productIds); // Update local state
  };

  const onConfirm = () => {
    const defaultVoucher = {
      id: selectedProductIds[0],
      qtyPerCollection: 0,
    };

    setVoucher(defaultVoucher);
    setIsShowVoucherDrawer(false);
    setIsShowVoucherDetailDrawer(true);
  };

  return (
    <>
      <ItemSelectionDrawer
        state={{
          data: selectedProductIds,
          form: form,
          onChange: onSelectChange,
        }}
        header={
          <SectionHeader
            title="Select Voucher"
            description={
              <Text>
                Only vouchers created specifically for referrals can be added here. Please create a referral-only
                voucher before continuing.
                <Button
                  variant="link"
                  color="primary"
                  style={{
                    marginLeft: "1rem",
                    color: "var(--ion-color-primary)",
                    textDecoration: "underline",
                    fontWeight: "bold",
                  }}
                  icon={<IonIcon icon={arrowForwardOutline} />}
                  iconPosition="end"
                  onClick={() => {
                    history.push("/merchantUser/product/request/new");
                  }}
                >
                  create product
                </Button>
              </Text>
            }
            style={{ margin: "0 0 0 1rem" }}
          />
        }
        items={productsToSelect} // Use filtered products instead of all products
        isShow={isShowVoucherDrawer}
        isLoading={isLoadingSubmit}
        isSingleSelect={true}
        tableType="product"
        confirmBtnText={"Next"}
        isConfirmBtnDisabled={selectedProductIds.length === 0}
        onClose={() => {
          setIsShowVoucherDrawer(false);
        }}
        onConfirm={onConfirm}
      />

      <VoucherDetailDrawer
        open={isShowVoucherDetailDrawer}
        isLoading={isLoadingSubmit}
        voucher={voucher}
        product={product}
        vouchers={data}
        header={
          <SectionHeader
            title="Voucher Detail"
            description={"Provide detail of the voucher"}
            style={{ margin: "0 0 0 1rem" }}
          />
        }
        confirmBtnText="Submit"
        onClose={() => {
          setVoucher();
          setIsShowVoucherDetailDrawer(false);
        }}
        onConfirm={(voucher) => {
          const existingIndex = data?.findIndex((item) => item.id === voucher.id);
          let updated = [...(data || [])];
          if (existingIndex !== -1) {
            updated[existingIndex] = voucher; // Replace the existing element
          } else {
            updated.push(voucher); // If it doesn't exist, add the new gift to the end of the array
          }

          //clear
          setSelectedProductIds([]);
          //
          setIsShowVoucherDetailDrawer(false);
          onSave(updated);
        }}
      />

      <Row justify="space-between" align="middle" style={{ width: "100%" }}>
        <Col>
          <SectionHeader title="Add Voucher" description="At least 1 type of voucher" />
        </Col>
        <Col>
          <Button
            size="large"
            type="primary"
            loading={isFetchingProducts}
            disabled={!canEdit}
            onClick={() => {
              setIsShowVoucherDrawer(true);
            }}
          >
            Add+
          </Button>
        </Col>
      </Row>

      <VoucherTable data={tableData} canDelete={canEdit} loading={isFetchingProducts} onDelete={onDelete} />
    </>
  );
};

export default SingleReferral;
